import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Eye, FileUp, ShieldCheck, Users, Building2, SlidersHorizontal, KeyRound, KeySquare, Sparkles, Database } from 'lucide-react';
import UsersTable from '../components/UsersTable';
import RolesTable from '../components/RolesTable';
import SitesTable from '../components/SitesTable';
import ConfigItemsModal from './Settings/ConfigItemsModal';
import SsoConfigModal from './Settings/SsoConfigModal';
import AiConfigModal from './Settings/AiConfigModal';
import RoleAccessModal from './Settings/RoleAccessModal';
import LicenseUploadModal from './Settings/LicenseUploadModal';
import LicenseViewModal from './Settings/LicenseViewModal';

import { ListSection, ListRow, PrimaryButton } from '../components/RowList';

// Shared modal shell: overlay + panel + header, with a scrollable body that
// hosts the relevant management table.
const SettingsModal = ({ title, onClose, children }) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-6 font-sans">
    <div className="bg-white dark:bg-slate-800 w-full max-w-6xl rounded-xl shadow-2xl flex flex-col h-[85vh] overflow-hidden">
      <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 dark:border-slate-700 shrink-0">
        <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">{title}</h2>
        <button
          onClick={onClose}
          aria-label="Close"
          className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 transition-colors text-lg leading-none"
        >
          ✕
        </button>
      </div>
      <div className="flex-1 overflow-y-auto bg-slate-100 dark:bg-slate-900">
        {children}
      </div>
    </div>
  </div>
);

const Settings = () => {
  // Which management modal is open: 'roles' | 'sites' | 'users' | 'ai-config' | null
  const [activeModal, setActiveModal] = useState(null);
  // The catalogue viewer lives on the Insights page rather than being duplicated
  // here - one rendering of it, scoped by the same role rules.
  const navigate = useNavigate();

  return (
    <div className="h-full overflow-y-auto font-sans bg-slate-100 dark:bg-slate-900">
      <div className="max-w-6xl mx-auto px-6 py-10">

        <div className="mb-10">
          <h1 className="m-0 text-3xl font-bold tracking-tight text-slate-900 dark:text-slate-100">System Settings</h1>
          <p className="mt-2 text-base text-slate-500 dark:text-slate-400">
            Manage system-wide options: Access Control, Organisation, Configuration, AI and Licensing.
          </p>
        </div>

        {/* SECTION 1: Access Control */}
        <ListSection title="Access Control">
          <ListRow
            icon={ShieldCheck}
            title="Roles"
            description="View, add, deactivate or update roles and the permissions attached to them."
            actions={<PrimaryButton onClick={() => setActiveModal('roles')}>Open</PrimaryButton>}
          />
          <ListRow
            icon={Users}
            title="User Accounts"
            description="View, add, deactivate or update user account records and their role assignments."
            actions={<PrimaryButton onClick={() => setActiveModal('users')}>Open</PrimaryButton>}
          />
          <ListRow
            icon={KeyRound}
            title="Role Data Access"
            description="Choose which Modules, Cost Centres and GL Accounts each role is allowed to access."
            actions={<PrimaryButton onClick={() => setActiveModal('role-access')}>Open</PrimaryButton>}
          />
        </ListSection>

        {/* SECTION 2: Organisation */}
        <ListSection title="Organisation">
          <ListRow
            icon={Building2}
            title="Sites"
            description="View, add, deactivate or update site and location master records."
            actions={<PrimaryButton onClick={() => setActiveModal('sites')}>Open</PrimaryButton>}
          />
        </ListSection>

        {/* SECTION 3: Configuration */}
        <ListSection title="Configuration">
          <ListRow
            icon={SlidersHorizontal}
            title="Config Items"
            description="System-wide settings such as the VAT rate and the financial-year start month."
            actions={<PrimaryButton onClick={() => setActiveModal('config')}>Open</PrimaryButton>}
          />
          <ListRow
            icon={KeySquare}
            title="Single Sign-On"
            description="Connect Mantle to your identity provider so people sign in with their work account. The client secret is stored write-only and is never shown again."
            actions={<PrimaryButton onClick={() => setActiveModal('sso')}>Open</PrimaryButton>}
          />
        </ListSection>

        {/* SECTION 4: AI Configuration
            Its own section rather than a row under Configuration: it holds a
            third-party credential and decides what leaves the building, which is
            a different kind of decision from the VAT rate. */}
        <ListSection title="AI Configuration">
          <ListRow
            icon={Sparkles}
            title="AI Provider & API Key"
            description="Set the service AI Insights talks to — its endpoint URL and API key — and which models people may choose. The key is encrypted before storage and is never shown again."
            actions={<PrimaryButton onClick={() => setActiveModal('ai-config')}>Open</PrimaryButton>}
          />
          <ListRow
            icon={Database}
            title="Data Catalogue"
            description="What the AI is told about your data: which tables answer which questions, what a row means, and where a query goes wrong. Maintained in the ai_catalogue_* tables and viewed from the Insights page."
            actions={<PrimaryButton onClick={() => navigate('/insights')}>View</PrimaryButton>}
          />
        </ListSection>

        {/* SECTION 5: Licensing */}
        <ListSection title="Licensing">
          <ListRow
            icon={FileUp}
            title="Upload License"
            description="Upload and validate an encrypted license file."
            actions={<PrimaryButton onClick={() => setActiveModal('upload-license')}>Open</PrimaryButton>}
          />
          <ListRow
            icon={Eye}
            title="View License"
            description="View the configuration of the current license."
            actions={<PrimaryButton onClick={() => setActiveModal('view-license')}>Open</PrimaryButton>}
          />
        </ListSection>

      </div>

      {/* Management modals */}
      {activeModal === 'roles' && (
        <SettingsModal title="Manage Roles" onClose={() => setActiveModal(null)}>
          <RolesTable />
        </SettingsModal>
      )}

      {activeModal === 'users' && (
        <SettingsModal title="Manage User Accounts" onClose={() => setActiveModal(null)}>
          <UsersTable />
        </SettingsModal>
      )}

      {activeModal === 'sites' && (
        <SettingsModal title="Manage Sites" onClose={() => setActiveModal(null)}>
          <SitesTable />
        </SettingsModal>
      )}

      {activeModal === 'config' && (
        <ConfigItemsModal onClose={() => setActiveModal(null)} />
      )}

      {activeModal === 'role-access' && (
        <RoleAccessModal onClose={() => setActiveModal(null)} />
      )}

      {activeModal === 'sso' && (
        <SsoConfigModal onClose={() => setActiveModal(null)} />
      )}

      {activeModal === 'ai-config' && (
        <AiConfigModal onClose={() => setActiveModal(null)} />
      )}

      {activeModal === 'upload-license' && (
        <LicenseUploadModal onClose={() => setActiveModal(null)} />
      )}

      {activeModal === 'view-license' && (
        <LicenseViewModal
          isOpen={true}
          onClose={() => setActiveModal(null)}
        />
      )}
    </div>
  );
};

export default Settings;
